import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaSave, FaPlus } from 'react-icons/fa';
import { getSizeChart, createSizeChart, updateSizeChart } from '../../../services/sizeChartService';

// Styled components
const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: white;
  color: #4f46e5;
  border: 1px solid #4f46e5;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #4338ca;
  }
  
  &:disabled {
    background-color: #c7d2fe;
    cursor: not-allowed;
  }
`;

const FormContainer = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin-top: 0;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 1px #4f46e5;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 1px #4f46e5;
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  cursor: pointer;
`;

const ImageUploadContainer = styled.div`
  margin-bottom: 16px;
`;

const ImagePreview = styled.div`
  margin-top: 8px;
  width: 100%;
  height: 200px;
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
`;

const ImagePreviewImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const ImageUploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f3f4f6;
  color: #374151;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const SizeDataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-weight: 500;
  font-size: 14px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 8px 12px;
  font-size: 14px;
  border-bottom: 1px solid #e5e7eb;
`;

const TableInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4f46e5;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  background-color: #dcfce7;
  color: #166534;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

// SizeChartForm Component
const SizeChartForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  
  // Image state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [topThumbnailFile, setTopThumbnailFile] = useState(null);
  const [topThumbnailPreview, setTopThumbnailPreview] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  
  // Size data state
  const [sizeDataInches, setSizeDataInches] = useState([
    { size: 'XS', chest: '', shoulder: '', length: '' },
    { size: 'S', chest: '', shoulder: '', length: '' },
    { size: 'M', chest: '', shoulder: '', length: '' },
    { size: 'L', chest: '', shoulder: '', length: '' },
    { size: 'XL', chest: '', shoulder: '', length: '' },
    { size: 'XXL', chest: '', shoulder: '', length: '' }
  ]);
  
  const [sizeDataCm, setSizeDataCm] = useState([
    { size: 'XS', chest: '', shoulder: '', length: '' },
    { size: 'S', chest: '', shoulder: '', length: '' },
    { size: 'M', chest: '', shoulder: '', length: '' },
    { size: 'L', chest: '', shoulder: '', length: '' },
    { size: 'XL', chest: '', shoulder: '', length: '' },
    { size: 'XXL', chest: '', shoulder: '', length: '' }
  ]);
  
  // Status state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Fetch size chart data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchSizeChart = async () => {
        try {
          setLoading(true);
          const response = await getSizeChart(id);
          
          if (response.success) {
            const { data } = response;
            
            // Set basic info
            setName(data.name);
            setDescription(data.description || '');
            setIsActive(data.isActive);
            
            // Set image previews
            setLogoPreview(data.logo.url);
            setTopThumbnailPreview(data.topThumbnail.url);
            setThumbnailPreview(data.thumbnail.url);
            
            // Set size data
            if (data.sizeDataInches && data.sizeDataInches.length > 0) {
              setSizeDataInches(data.sizeDataInches);
            }
            
            if (data.sizeDataCm && data.sizeDataCm.length > 0) {
              setSizeDataCm(data.sizeDataCm);
            }
          } else {
            setError('Failed to load size chart data');
          }
        } catch (err) {
          console.error('Error fetching size chart:', err);
          setError('Failed to load size chart data');
        } finally {
          setLoading(false);
        }
      };
      
      fetchSizeChart();
    }
  }, [id, isEditMode]);
  
  // Handle image file changes
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };
  
  const handleTopThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTopThumbnailFile(file);
      setTopThumbnailPreview(URL.createObjectURL(file));
    }
  };
  
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };
  
  // Handle size data changes
  const handleSizeDataInchesChange = (index, field, value) => {
    const newData = [...sizeDataInches];
    newData[index][field] = value;
    setSizeDataInches(newData);
  };
  
  const handleSizeDataCmChange = (index, field, value) => {
    const newData = [...sizeDataCm];
    newData[index][field] = value;
    setSizeDataCm(newData);
  };
  
  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (!isEditMode && (!logoFile || !topThumbnailFile || !thumbnailFile)) {
      setError('All images are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Create FormData object
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('isActive', isActive);
      formData.append('sizeDataInches', JSON.stringify(sizeDataInches));
      formData.append('sizeDataCm', JSON.stringify(sizeDataCm));
      
      // Add image files if available
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      if (topThumbnailFile) {
        formData.append('topThumbnail', topThumbnailFile);
      }
      
      if (thumbnailFile) {
        formData.append('thumbnail', thumbnailFile);
      }
      
      // Submit form
      let response;
      if (isEditMode) {
        response = await updateSizeChart(id, formData);
      } else {
        response = await createSizeChart(formData);
      }
      
      if (response.success) {
        setSuccess(`Size chart ${isEditMode ? 'updated' : 'created'} successfully`);
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/admin/size-charts');
        }, 1500);
      } else {
        setError(response.error || `Failed to ${isEditMode ? 'update' : 'create'} size chart`);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} size chart:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} size chart`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>{isEditMode ? 'Edit Size Chart' : 'Add Size Chart'}</Title>
        <div style={{ display: 'flex', gap: '12px' }}>
          <BackButton onClick={() => navigate('/admin/size-charts')}>
            <FaArrowLeft size={14} />
            Back
          </BackButton>
          <SaveButton 
            onClick={handleSubmit} 
            disabled={loading}
          >
            <FaSave size={14} />
            {loading ? 'Saving...' : 'Save'}
          </SaveButton>
        </div>
      </Header>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      
      <FormContainer onSubmit={handleSubmit}>
        {/* Basic Information */}
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="name">Name*</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., T-Shirt Size Chart"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="description">Description</Label>
            <TextArea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide a brief description of this size chart"
            />
          </FormGroup>
          
          <FormGroup>
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </CheckboxLabel>
          </FormGroup>
        </FormSection>
        
        {/* Images */}
        <FormSection>
          <SectionTitle>Images</SectionTitle>
          
          <ImageUploadContainer>
            <Label>Logo (TMZ Logo)*</Label>
            <ImagePreview>
              {logoPreview ? (
                <ImagePreviewImg src={logoPreview} alt="Logo Preview" />
              ) : (
                <span>No image selected</span>
              )}
            </ImagePreview>
            <ImageUploadLabel>
              <FaPlus size={14} />
              {logoPreview ? 'Change Logo' : 'Upload Logo'}
              <HiddenInput
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
              />
            </ImageUploadLabel>
          </ImageUploadContainer>
          
          <ImageUploadContainer>
            <Label>Top Thumbnail (Ruler Icon)*</Label>
            <ImagePreview>
              {topThumbnailPreview ? (
                <ImagePreviewImg src={topThumbnailPreview} alt="Top Thumbnail Preview" />
              ) : (
                <span>No image selected</span>
              )}
            </ImagePreview>
            <ImageUploadLabel>
              <FaPlus size={14} />
              {topThumbnailPreview ? 'Change Top Thumbnail' : 'Upload Top Thumbnail'}
              <HiddenInput
                type="file"
                accept="image/*"
                onChange={handleTopThumbnailChange}
              />
            </ImageUploadLabel>
          </ImageUploadContainer>
          
          <ImageUploadContainer>
            <Label>Thumbnail (Measurement Guide)*</Label>
            <ImagePreview>
              {thumbnailPreview ? (
                <ImagePreviewImg src={thumbnailPreview} alt="Thumbnail Preview" />
              ) : (
                <span>No image selected</span>
              )}
            </ImagePreview>
            <ImageUploadLabel>
              <FaPlus size={14} />
              {thumbnailPreview ? 'Change Thumbnail' : 'Upload Thumbnail'}
              <HiddenInput
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
              />
            </ImageUploadLabel>
          </ImageUploadContainer>
        </FormSection>
        
        {/* Size Data in Inches */}
        <FormSection>
          <SectionTitle>Size Data (Inches)</SectionTitle>
          
          <SizeDataTable>
            <thead>
              <tr>
                <TableHeader>Size</TableHeader>
                <TableHeader>Chest</TableHeader>
                <TableHeader>Shoulder</TableHeader>
                <TableHeader>Length</TableHeader>
              </tr>
            </thead>
            <tbody>
              {sizeDataInches.map((row, index) => (
                <tr key={index}>
                  <TableCell>{row.size}</TableCell>
                  <TableCell>
                    <TableInput
                      type="text"
                      value={row.chest}
                      onChange={(e) => handleSizeDataInchesChange(index, 'chest', e.target.value)}
                      placeholder='e.g., 43"'
                    />
                  </TableCell>
                  <TableCell>
                    <TableInput
                      type="text"
                      value={row.shoulder}
                      onChange={(e) => handleSizeDataInchesChange(index, 'shoulder', e.target.value)}
                      placeholder='e.g., 17"'
                    />
                  </TableCell>
                  <TableCell>
                    <TableInput
                      type="text"
                      value={row.length}
                      onChange={(e) => handleSizeDataInchesChange(index, 'length', e.target.value)}
                      placeholder='e.g., 29.5"'
                    />
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </SizeDataTable>
        </FormSection>
        
        {/* Size Data in CM */}
        <FormSection>
          <SectionTitle>Size Data (CM)</SectionTitle>
          
          <SizeDataTable>
            <thead>
              <tr>
                <TableHeader>Size</TableHeader>
                <TableHeader>Chest</TableHeader>
                <TableHeader>Shoulder</TableHeader>
                <TableHeader>Length</TableHeader>
              </tr>
            </thead>
            <tbody>
              {sizeDataCm.map((row, index) => (
                <tr key={index}>
                  <TableCell>{row.size}</TableCell>
                  <TableCell>
                    <TableInput
                      type="text"
                      value={row.chest}
                      onChange={(e) => handleSizeDataCmChange(index, 'chest', e.target.value)}
                      placeholder="e.g., 109.2 cm"
                    />
                  </TableCell>
                  <TableCell>
                    <TableInput
                      type="text"
                      value={row.shoulder}
                      onChange={(e) => handleSizeDataCmChange(index, 'shoulder', e.target.value)}
                      placeholder="e.g., 43.2 cm"
                    />
                  </TableCell>
                  <TableCell>
                    <TableInput
                      type="text"
                      value={row.length}
                      onChange={(e) => handleSizeDataCmChange(index, 'length', e.target.value)}
                      placeholder="e.g., 74.9 cm"
                    />
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </SizeDataTable>
        </FormSection>
      </FormContainer>
    </Container>
  );
};

export default SizeChartForm; 